import React, { createContext, useState, useCallback, useEffect, useRef } from "react";
import { useHttp } from "../hooks/useHttp";
import { API_URLS, DEFAULT_HEADERS, STORAGE_KEYS, TIMEOUTS } from "../util/constants";
import { handleApiError } from "../util/errorHandler";
import { useLocalStorage } from "../hooks/useLocalStorage";

export const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [assistedPurchaseState, setAssistedPurchaseState] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  // eslint-disable-next-line no-unused-vars
  
  const logoutTimerRef = useRef(null);
  const hasCheckedAutoLoginRef = useRef(false);
  const { sendRequest } = useHttp();
  const [storedUserData, setStoredUserData] = useLocalStorage(STORAGE_KEYS.USER_DATA, null);

  const loadUserInfo = useCallback(
    async (uid) => {
      try {
        const responseData = await sendRequest(
          API_URLS.GET_USER_INFO,
          "POST",
          JSON.stringify({
            user_id: parseInt(uid),
          }),
          DEFAULT_HEADERS.JSON_WITH_CORS
        );
        if (responseData.success) {
          setUserInfo(responseData.content);
        } else {
          throw new Error(responseData.error);
        }
      } catch (error) {
        handleApiError(error, { context: "AuthContext.loadUserInfo", showToast: false });
      }
    },
    [sendRequest]
  );

  // Limpa o timer de logout
  const clearLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  // Configura o timer de logout
  const setupLogoutTimer = useCallback((expirationDate) => {
    clearLogoutTimer();
    if (expirationDate) {
      const remainingTime = expirationDate.getTime() - new Date().getTime();
      if (remainingTime > 0) {
        logoutTimerRef.current = setTimeout(() => {
          // Logout automático quando token expira
          setUser(null);
          setIsLoggedIn(false);
          setUserInfo(null);
          setStoredUserData(null);
        }, remainingTime);
      }
    }
  }, [clearLogoutTimer, setStoredUserData]);

  // Função de login centralizada
  const login = useCallback(
    (uid, token, cashback, is_user_terra, expirationDate) => {
      const tokenExpirationTime =
        expirationDate || new Date(new Date().getTime() + TIMEOUTS.TOKEN_EXPIRATION);
      
      setUser({
        uid,
        token,
        cashback,
        is_user_terra,
      });
      setIsLoggedIn(true);
      
      // Salva no localStorage
      setStoredUserData({
        userId: uid,
        token: token,
        is_user_terra: is_user_terra,
        expiration: tokenExpirationTime.toISOString(),
        cashback: cashback,
      });

      setupLogoutTimer(tokenExpirationTime);
    },
    [setStoredUserData, setupLogoutTimer]
  );

  // Função de logout centralizada
  const logout = useCallback(() => {
    clearLogoutTimer();
    setUser(null);
    setIsLoggedIn(false);
    setUserInfo(null);
    setAssistedPurchaseState(null);
    setStoredUserData(null);
  }, [clearLogoutTimer, setStoredUserData]);

  // Carrega userInfo quando user muda
  useEffect(() => {
    if (user?.uid) {
      loadUserInfo(user.uid);
    } else {
      setUserInfo(null);
    }
  }, [loadUserInfo, user]);

  // Auto-login ao carregar a página (executa apenas uma vez)
  useEffect(() => {
    // Evita múltiplas execuções
    if (hasCheckedAutoLoginRef.current) {
      return;
    }

    hasCheckedAutoLoginRef.current = true;

    // Se já está logado, não precisa verificar
    if (isLoggedIn && user) {
      setIsCheckingAuth(false);
      return;
    }

    // Se não há dados salvos, finaliza verificação
    if (!storedUserData) {
      setIsCheckingAuth(false);
      return;
    }

    // Verifica se token é válido
    try {
      const expiration = storedUserData.expiration
        ? new Date(storedUserData.expiration)
        : null;

      if (
        storedUserData.token &&
        storedUserData.userId &&
        expiration &&
        expiration > new Date()
      ) {
        // Token válido - faz login automático
        login(
          storedUserData.userId,
          storedUserData.token,
          storedUserData.cashback,
          storedUserData.is_user_terra,
          expiration
        );
      } else {
        // Token expirado - limpa dados
        setStoredUserData(null);
      }
    } catch (error) {
      console.error("Erro ao verificar dados salvos:", error);
      setStoredUserData(null);
    } finally {
      setIsCheckingAuth(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executa apenas uma vez na montagem

  // Cleanup do timer ao desmontar
  useEffect(() => {
    return () => {
      clearLogoutTimer();
    };
  }, [clearLogoutTimer]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        user,
        setUser,
        loadUserInfo,
        userInfo,
        setUserInfo,
        setAssistedPurchaseState,
        assistedPurchaseState,
        login,
        logout,
        isCheckingAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
